import React, { useMemo, useCallback, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";

const UserAccountsTable = ({ userAccounts, onRowClick }) => {
  const { t } = useTranslation(["auth"]);
  const [pageSize, setPageSize] = useState(20);

  // Memoize column definitions to prevent unnecessary re-renders
  const columnDefs = useMemo(
    () => [
      // The type will be automatically detected as 'userColumn' based on field name
      {
        field: "login",
        headerName: t("Login"),
        filter: true,
        sortable: true,
        flex: 1.5,
      },
      {
        field: "firstName",
        headerName: t("FirstName"),
        filter: true,
        sortable: true,
        flex: 1.5,
      },
      {
        field: "lastName",
        headerName: t("LastName"),
        filter: true,
        sortable: true,
        flex: 1.5,
      },
      // The type will be automatically detected as 'emailColumn' based on field name
      {
        field: "email",
        headerName: t("Email"),
        filter: true,
        sortable: true,
        flex: 2,
      },
      {
        field: "active",
        headerName: t("Active"),
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        // Apply centered class for better alignment
        headerClass: "ag-center-aligned-header",
        filter: true,
        width: 100,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
    ],
    [t]
  ); // Re-create only when translations change

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      sortable: true,
      filter: true,
    }),
    []
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      // Enable pagination for all tables regardless of size
      enabled: true,
      // Default page size
      pageSize: 20,
      // Available page size options for user to choose
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    []
  );

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
  }, []);

  return (
    <ThemeableAgGrid
      className="table-bold clickable"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowData={userAccounts}
      onRowClicked={(params) => onRowClick && onRowClick(params.data)}
      getRowId={(params) => params.data.userAccountId.toString()}
      domLayout="autoHeight"
      // Enable pagination
      pagination={true}
      paginationPageSize={pageSize}
      paginationPageSizeSelector={paginationConfig.pageSizeOptions}
      onPaginationChanged={(event) => {
        const newPageSize = event.api.paginationGetPageSize();
        if (newPageSize !== pageSize) {
          handlePageSizeChange(newPageSize);
        }
      }}
      suppressPaginationPanel={false}
      animateRows={true}
    />
  );
};

export default React.memo(UserAccountsTable);

